import moment from 'moment';
import { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, Grid, Pagination, TextField, Typography } from '@mui/material';
import { isEmpty, isNil, map } from 'lodash';
import { LoadingButton } from '@mui/lab';
import CircularProgress from '@mui/material/CircularProgress';

const PatientPrescriptionNotes = ({ notes, onAddNote }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const notesPerPage = 5;

  const sortedNotes = useMemo(() => {
    if (isEmpty(notes)) return [];
    return [...notes].sort(
      (a, b) => moment(b.noteCreatedAt).valueOf() - moment(a.noteCreatedAt).valueOf()
    );
  }, [notes]);

  const totalPages = useMemo(
    () => Math.ceil((sortedNotes?.length || 0) / notesPerPage),
    [sortedNotes]
  );

  const paginatedNotes = useMemo(() => {
    if (isEmpty(sortedNotes)) return [];
    const startIndex = (currentPage - 1) * notesPerPage;
    const endIndex = startIndex + notesPerPage;
    return sortedNotes.slice(startIndex, endIndex);
  }, [sortedNotes, currentPage]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const addNotes = () => {
    setLoading(true);
    return onAddNote(message, (success) => {
      if (success) {
        setMessage('');
        setLoading(false);
      }
    });
  };

  return (
    <>
      <style>
        {`
        @media print {
          .addNoteInput {
            display: none;
          }
        }
  `}
      </style>
      <Typography variant='h6' sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
        Notes
      </Typography>
      <Divider />

      <Grid container direction='column' sx={{ mb: 3 }} gap={1} className='addNoteInput'>
        <Grid item>
          <Typography
            variant='subtitle2'
            alignItems='flex-start'
            sx={{ mt: 2, fontWeight: 600, fontSize: '0.8rem' }}
          >
            Add
          </Typography>
        </Grid>
        <Grid item>
          <TextField
            id='outlined-basic'
            fullWidth
            variant='outlined'
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Grid>
        <Grid container direction='row' justifyContent='flex-end'>
          <LoadingButton
            loading={loading}
            variant='contained'
            disabled={isEmpty(message)}
            onClick={addNotes}
            size='small'
            sx={{
              px: 1,
              minHeight: 30,
              fontSize: '0.75rem',
              lineHeight: 'normal',
              minWidth: '5',
              textTransform: 'none',
            }}
          >
            Add
          </LoadingButton>
        </Grid>
      </Grid>

      <Divider />
      {!isEmpty(notes) ? (
        <>
          {map(paginatedNotes, ({ noteId, noteAuthor, noteCreatedAt, noteContent }) => (
            <Fragment key={noteId}>
              <Grid
                sx={{ mt: 1 }}
                container
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography variant='body2' sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {`${noteAuthor.firstName} ${noteAuthor.middleName || ''} ${
                    noteAuthor.lastName || ''
                  } `}
                </Typography>
                <Typography variant='caption' sx={{ fontSize: '0.75rem' }}>
                  {moment(noteCreatedAt).format('hh:mma  MM/DD/YYYY')}
                </Typography>
              </Grid>
              <Typography variant='body2' sx={{ mt: 2, fontSize: '0.8rem' }}>
                {noteContent}
              </Typography>
              <Divider />
            </Fragment>
          ))}
          {totalPages > 1 && (
            <Grid container justifyContent='flex-end' sx={{ mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                size='small'
                onChange={handlePageChange}
              />
            </Grid>
          )}
        </>
      ) : (
        <Grid container direction='row' justifyContent='center' alignItems='center' sx={{ mt: 3 }}>
          {isNil(notes) ? (
            <CircularProgress />
          ) : (
            <Typography variant='body2' sx={{ mt: 2, textAlign: 'center', fontSize: '0.8rem' }}>
              Nothing Found...
            </Typography>
          )}
        </Grid>
      )}
    </>
  );
};

export default PatientPrescriptionNotes;

PatientPrescriptionNotes.propTypes = {
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      noteId: PropTypes.string.isRequired,
      noteContent: PropTypes.string.isRequired,
      noteCreatedAt: PropTypes.string.isRequired,
      noteAuthor: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        middleName: PropTypes.string,
      }).isRequired,
    })
  ),
  onAddNote: PropTypes.func.isRequired,
};

PatientPrescriptionNotes.defaultProps = {
  notes: [],
};
